import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Cloudflare Turnstile component
declare global {
  interface Window {
    turnstile: {
      render: (element: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export const Login: React.FC = () => {
  const { login, verifyCode } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Use ref to track widget ID reliably across renders
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const isWidgetRenderingRef = useRef(false);

  // Clean up Turnstile widget
  const cleanupTurnstileWidget = () => {
    if (turnstileWidgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      } catch (e) {
        console.warn('Failed to remove Turnstile widget:', e);
      }
      turnstileWidgetIdRef.current = null;
    }
    
    // Clear the container
    const container = document.getElementById('turnstile-widget');
    if (container) {
      container.innerHTML = '';
    }
    
    isWidgetRenderingRef.current = false;
  };

  // Render Turnstile widget
  const renderTurnstileWidget = () => {
    if (isWidgetRenderingRef.current || !window.turnstile) {
      return;
    }
    
    isWidgetRenderingRef.current = true;
    setError('');
    
    try {
      const widgetId = window.turnstile.render('#turnstile-widget', {
        sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '',
        callback: (token: string) => {
          setTurnstileToken(token);
          setError(''); // Clear any previous error messages
        },
        'error-callback': () => {
          // Only show error if we're not already cleaning up
          if (!isWidgetRenderingRef.current) {
            setError('Security check had an issue. Please refresh the page if you need to login.');
          }
          setTurnstileToken('');
        },
        'expired-callback': () => {
          // Token expired naturally - reset quietly without error message
          setTurnstileToken('');
          // Don't show error for natural expiration
        },
        'timeout-callback': () => {
          // Timeout occurred - reset quietly
          setTurnstileToken('');
          // Don't show error for timeout
        },
      });
      turnstileWidgetIdRef.current = widgetId;
    } catch (e) {
      console.error('Failed to render Turnstile widget:', e);
      setError('Security check had an issue. Please refresh the page if you need to login.');
      isWidgetRenderingRef.current = false;
    }
  };

  // Load Turnstile script
  useEffect(() => {
    // Skip if Turnstile is disabled
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY === 'xxxxx') {
      return;
    }

    // Clean up any existing widget first
    cleanupTurnstileWidget();

    // Check if script is already loaded
    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
      // Script already exists, just render the widget
      if (window.turnstile) {
        renderTurnstileWidget();
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      if (window.turnstile) {
        renderTurnstileWidget();
      }
    };

    script.onerror = () => {
      setError('Failed to load security check. Please refresh the page.');
      isWidgetRenderingRef.current = false;
    };

    return () => {
      cleanupTurnstileWidget();
    };
  }, []); // Empty dependency array - only run once on mount

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email format
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (!turnstileToken && import.meta.env.VITE_TURNSTILE_SITE_KEY !== 'xxxxx') {
      setError('Please complete the security verification.');
      return;
    }

    setLoading(true);
    setError('');
    setEmailError('');

    try {
      await login(email, turnstileToken);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send login code');
      // Reset Turnstile widget when there's an error
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        } catch (e) {
          console.warn('Failed to reset Turnstile widget:', e);
        }
        setTurnstileToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await verifyCode(email, code);
      // Navigation will be handled by the router
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setTurnstileToken('');
    
    // Clean up and re-render Turnstile widget
    cleanupTurnstileWidget();
    
    // Re-render the widget after a short delay
    setTimeout(() => {
      if (window.turnstile && import.meta.env.VITE_TURNSTILE_SITE_KEY !== 'xxxxx') {
        renderTurnstileWidget();
      }
    }, 200);
  };

  return (
    <div className="min-h-screen flex items-start justify-center bg-[#2F4F6F] px-4 sm:px-6 py-12 sm:py-16 pt-12 sm:pt-20">
      <div className="bg-white rounded-xl p-6 sm:p-8 md:p-10 shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <img 
            src="/Final3.png" 
            alt="Sepori Capital" 
            className="w-64 sm:w-72 md:w-80 h-auto object-contain" 
          />
        </div>
        <p className="text-center text-gray-600 mb-6 sm:mb-8 text-base sm:text-lg font-bold">
          Sign in to access the portal!
        </p>

        {step === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4 sm:gap-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="font-medium text-gray-600 text-sm">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) {
                    setEmailError('');
                  }
                }}
                placeholder="Enter your email"
                required
                disabled={loading}
                className={`px-3 sm:px-4 py-3 border-2 rounded-lg text-base bg-white text-gray-900 placeholder-gray-400 transition-colors focus:outline-none focus:border-[#70B8B8] disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  emailError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {emailError && (
                <span className="text-sm text-red-500 mt-1">{emailError}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div id="turnstile-widget" className="flex justify-center"></div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-3 sm:px-4 py-3 rounded-md text-sm border border-red-200 text-center whitespace-pre-line">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading || !turnstileToken} 
              className="bg-[#2F4F6F] text-white border-none px-6 py-3.5 rounded-lg text-base font-semibold cursor-pointer transition-opacity disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90"
            >
              {loading ? 'Sending...' : !turnstileToken ? 'Complete Security Check First' : 'Send Login Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit} className="flex flex-col gap-4 sm:gap-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="code" className="font-medium text-gray-600 text-sm">
                6-Digit Code
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength={6}
                required
                disabled={loading}
                className="px-3 sm:px-4 py-3 border-2 border-gray-300 rounded-lg text-center text-xl sm:text-2xl tracking-widest font-semibold bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#70B8B8] disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="mt-2 text-gray-600 text-sm text-center">
                We sent a 6-digit code to <strong>{email}</strong>
              </p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-3 sm:px-4 py-3 rounded-md text-sm border border-red-200 text-center whitespace-pre-line">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                type="button" 
                onClick={handleBackToEmail} 
                className="bg-gray-100 text-gray-600 border border-gray-300 px-6 py-3.5 rounded-lg text-base cursor-pointer transition-colors hover:bg-gray-200 flex-1"
              >
                Back to Email
              </button>
              <button 
                type="submit" 
                disabled={loading || code.length !== 6} 
                className="bg-[#2F4F6F] text-white border-none px-6 py-3.5 rounded-lg text-base font-semibold cursor-pointer transition-opacity disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90 flex-1"
              >
                {loading ? 'Verifying...' : 'Sign In'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
